import React, { useState, useEffect, useRef, useCallback } from "react";
import { searchLabels, createLabel } from "../../services/labelService";
import styles from "./LabelSelector.module.css";

/**
 * LabelSelector - A GitHub-style label selector component
 * 
 * Props:
 * - selectedLabels: Array of label objects currently selected
 * - onChange: Callback when labels change, receives array of label objects
 * - disabled: Boolean to disable the component
 * - placeholder: Placeholder text for input
 */
export default function LabelSelector({
  selectedLabels = [],
  onChange,
  disabled = false,
  placeholder = "Add labels...",
}) {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Fetch suggestions when input changes
  const fetchSuggestions = useCallback(async (query) => {
    try {
      setLoading(true);
      const data = await searchLabels(query);
      setSuggestions(data.labels || []);
    } catch (err) {
      console.error("Error fetching label suggestions:", err);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (isOpen) {
      debounceRef.current = setTimeout(() => {
        fetchSuggestions(inputValue);
      }, 200);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [inputValue, isOpen, fetchSuggestions]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Check if a label is selected
  const isSelected = (label) => {
    return selectedLabels.some((l) => l.label_id === label.label_id);
  };

  // Filter out already selected labels from suggestions
  const filteredSuggestions = suggestions.filter((s) => !isSelected(s));

  // Check if the typed value matches any existing label
  const exactMatch = suggestions.find(
    (s) => s.name.toLowerCase() === inputValue.trim().toLowerCase()
  );

  // Show create option if typing a new label name
  const showCreateOption = inputValue.trim() && !exactMatch && !disabled;

  // Toggle label selection
  const toggleLabel = (label) => {
    if (isSelected(label)) {
      onChange(selectedLabels.filter((l) => l.label_id !== label.label_id));
    } else {
      onChange([...selectedLabels, label]);
    }
    setInputValue("");
    setHighlightedIndex(-1);
  };

  // Remove a label
  const removeLabel = (labelId) => {
    onChange(selectedLabels.filter((l) => l.label_id !== labelId));
  };

  // Create a new label and add it
  const handleCreateLabel = async () => {
    if (!inputValue.trim()) return;

    try {
      const data = await createLabel({ name: inputValue.trim() });
      if (data.label) {
        onChange([...selectedLabels, data.label]);
        setInputValue("");
        // Refresh suggestions to include new label
        fetchSuggestions("");
      }
    } catch (err) {
      console.error("Error creating label:", err);
      alert(err.message || "Failed to create label");
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    const totalItems = filteredSuggestions.length + (showCreateOption ? 1 : 0);

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => 
          prev < totalItems - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => 
          prev > 0 ? prev - 1 : totalItems - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0) {
          if (highlightedIndex < filteredSuggestions.length) {
            toggleLabel(filteredSuggestions[highlightedIndex]);
          } else if (showCreateOption) {
            handleCreateLabel();
          }
        } else if (showCreateOption) {
          handleCreateLabel();
        }
        break;
      case "Escape":
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      case "Backspace":
        if (!inputValue && selectedLabels.length > 0) {
          removeLabel(selectedLabels[selectedLabels.length - 1].label_id);
        }
        break;
      default:
        break;
    }
  };

  // Get contrasting text color for label background
  const getTextColor = (bgColor) => {
    if (!bgColor) return '#ffffff';
    const hex = bgColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#1f2937' : '#ffffff';
  };

  return (
    <div className={styles.labelSelector} ref={containerRef}>
      <div 
        className={`${styles.inputWrapper} ${disabled ? styles.disabled : ''}`}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        {/* Input row */}
        <input
          ref={inputRef}
          type="text"
          className={styles.labelInput}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
        />
        {/* Selected labels row */}
        {selectedLabels.length > 0 && (
          <div className={styles.selectedLabels}>
            {selectedLabels.map((label) => (
              <span
                key={label.label_id}
                className={styles.labelTag}
                style={{ 
                  backgroundColor: label.color || '#6b7280',
                  color: getTextColor(label.color)
                }}
              >
                {label.name}
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeLabel(label.label_id);
                    }}
                    aria-label={`Remove ${label.name}`}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
      </div>

      {isOpen && !disabled && (
        <div className={styles.dropdown}>
          {filteredSuggestions.length === 0 && !showCreateOption && !loading && (
            <div className={styles.noResults}>
              {inputValue ? "No matching labels" : "No labels yet"}
            </div>
          )}

          {filteredSuggestions.map((label, index) => (
            <div
              key={label.label_id}
              className={`${styles.dropdownItem} ${
                highlightedIndex === index ? styles.highlighted : ''
              } ${isSelected(label) ? styles.selected : ''}`}
              onClick={() => toggleLabel(label)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <span 
                className={styles.labelDot} 
                style={{ backgroundColor: label.color || '#6b7280' }}
              />
              <span className={styles.labelName}>{label.name}</span>
              {isSelected(label) && <span className={styles.checkmark}>✓</span>}
            </div>
          ))}

          {showCreateOption && (
            <div
              className={`${styles.createOption} ${
                highlightedIndex === filteredSuggestions.length ? styles.highlighted : ''
              }`}
              onClick={handleCreateLabel}
              onMouseEnter={() => setHighlightedIndex(filteredSuggestions.length)}
            >
              <span className={styles.createIcon}>+</span>
              Create "{inputValue.trim()}" <span className={styles.hintText}>(Press Enter)</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
